import { Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { getCurrentUser, ensureWorkspace, getImpersonationStatus } from './lib/api'
import Layout from './components/Layout'
import Home from './pages/Home'
import Workspace from './pages/Workspace'
import MyApps from './pages/MyApps'
import AppDetail from './pages/AppDetail'
import Settings from './pages/Settings'
import Changelog from './pages/Changelog'
import ChangelogDetail from './pages/ChangelogDetail'
import Guide from './pages/Guide'
import AdminUsers from './pages/admin/Users'
import AdminUserDetail from './pages/admin/UserDetail'
import AdminOverview from './pages/admin/Overview'
import AdminActivity from './pages/admin/Activity'
import AdminApps from './pages/admin/Apps'
import AdminWorkspaces from './pages/admin/Workspaces'
import AdminPlatformSettings from './pages/admin/PlatformSettings'
import AdminResourceLimits from './pages/admin/ResourceLimits'
import AdminClaudeCode from './pages/admin/ClaudeCode'
import AdminSecretFindings from './pages/admin/SecretFindings'
import AnalyticsOverview from './pages/admin/analytics/AnalyticsOverview'
import UserAnalytics from './pages/admin/analytics/UserAnalytics'
import AppAnalytics from './pages/admin/analytics/AppAnalytics'
import AnalyticsActivityFeed from './pages/admin/analytics/ActivityFeed'
import FeatureAdoption from './pages/admin/analytics/FeatureAdoption'
import SystemObservability from './pages/admin/analytics/SystemObservability'
import CreatorDashboard from './pages/CreatorDashboard'
import BrowseApps from './pages/BrowseApps'
import { UserContext, UserContextType } from './hooks/useUser'
import { SettingsProvider } from './hooks/useSettings'
import { WhatsNewProvider } from './hooks/useWhatsNew'
import { useThemeEffect } from './hooks/useThemeEffect'

// Component to apply theme effect (must be inside SettingsProvider)
function ThemeApplicator() {
  useThemeEffect()
  return null
}

function App() {
  const [user, setUser] = useState<UserContextType | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    Promise.all([
      getCurrentUser(),
      getImpersonationStatus(),
    ])
      .then(async ([userData, impersonationData]) => {
        setUser({
          email: userData.email,
          name: userData.name,
          firstName: userData.first_name,
          lastName: userData.last_name,
          services: userData.services,
          isAdmin: userData.is_admin,
          impersonation: {
            impersonating: impersonationData.impersonating,
            originalAdmin: impersonationData.original_admin,
            targetUser: impersonationData.target_user,
          },
        })
        // Ensure workspace container exists
        try {
          await ensureWorkspace()
        } catch (err) {
          console.error('Failed to ensure workspace:', err)
        }
      })
      .catch((err) => {
        console.error('Failed to get user:', err)
        // Redirect to login
        window.location.href = '/login'
      })
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!user) {
    return null
  }

  return (
    <UserContext.Provider value={user}>
      <SettingsProvider>
        <WhatsNewProvider>
          <ThemeApplicator />
          <Routes>
            {/* Full-screen workspace page (no Layout) */}
            <Route path="/workspace" element={<Workspace />} />

            {/* Standard pages with sidebar Layout */}
            <Route path="/*" element={
              <Layout>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/apps" element={<MyApps />} />
                  <Route path="/apps/:appName" element={<AppDetail />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/changelog" element={<Changelog />} />
                  <Route path="/changelog/:slug" element={<ChangelogDetail />} />
                  <Route path="/guide" element={<Guide />} />
                  <Route path="/guide/:section" element={<Guide />} />
                  <Route path="/browse" element={<BrowseApps />} />
                  <Route path="/creator-dashboard" element={<CreatorDashboard />} />
                  {user.isAdmin && (
                    <>
                      <Route path="/admin" element={<AdminOverview />} />
                      <Route path="/admin/users" element={<AdminUsers />} />
                      <Route path="/admin/users/:userId" element={<AdminUserDetail />} />
                      <Route path="/admin/workspaces" element={<AdminWorkspaces />} />
                      <Route path="/admin/apps" element={<AdminApps />} />
                      <Route path="/admin/activity" element={<AdminActivity />} />
                      <Route path="/admin/secret-findings" element={<AdminSecretFindings />} />
                      <Route path="/admin/platform-settings" element={<AdminPlatformSettings />} />
                      <Route path="/admin/resource-limits" element={<AdminResourceLimits />} />
                      <Route path="/admin/claude-code" element={<AdminClaudeCode />} />
                      <Route path="/admin/analytics" element={<AnalyticsOverview />} />
                      <Route path="/admin/analytics/users" element={<UserAnalytics />} />
                      <Route path="/admin/analytics/apps" element={<AppAnalytics />} />
                      <Route path="/admin/analytics/activity" element={<AnalyticsActivityFeed />} />
                      <Route path="/admin/analytics/features" element={<FeatureAdoption />} />
                      <Route path="/admin/analytics/system" element={<SystemObservability />} />
                    </>
                  )}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </Layout>
            } />
          </Routes>
        </WhatsNewProvider>
      </SettingsProvider>
    </UserContext.Provider>
  )
}

export default App
// Dashboard rebuild trigger
